import { t } from 'i18next';
import type { IObservableArray } from 'mobx';
import { runInAction } from 'mobx';
import { useCallback, useRef } from 'react';

import type { Recommendation } from '@feathr/blackbox';
import { ERecommendationStatus } from '@feathr/blackbox';
import { toast } from '@feathr/components';
import { useStore } from '@feathr/extender/state';

interface IUseDismissRecommendationProps {
  /** The recommendation models */
  recommendations: IObservableArray<Recommendation>;
  /** Refreshes the recommendations collection to see if any recommendation statuses have changed */
  onRefreshCollection: () => Promise<void>;
}

interface IUseDismissRecommendationReturnProps {
  /** Function to dismiss a recommendation by ID */
  onDismissRecommendation: (id: string) => Promise<void>;
}

/**
 * Hook to manage dismissing recommendations
 * @param recommendations - The recommendation models
 * @param onRefreshCollection - Function to refresh the recommendations collection
 * @returns Function to dismiss recommendations
 */
function useDismissRecommendation({
  recommendations,
  onRefreshCollection,
}: IUseDismissRecommendationProps): IUseDismissRecommendationReturnProps {
  const { UserNotifications } = useStore();
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced notification refresh to prevent flash during rapid dismissals
  const debouncedNotificationRefresh = useCallback(() => {
    // Clear any existing timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    // Schedule refresh after UI has had time to update to prevent a flash
    refreshTimeoutRef.current = setTimeout(() => {
      requestAnimationFrame(() => {
        UserNotifications.refreshApiCache();
      });
    }, 1000);
  }, [UserNotifications]);

  async function handleDismissRecommendation(id: string): Promise<void> {
    const recommendation = recommendations.find((rec) => rec.id === id);

    if (!recommendation) {
      toast(t('Unable to find recommendation to dismiss'), { type: 'error' });
      return;
    }

    try {
      // Only change the status if we're about to attempt the patch
      recommendation.set({ status: ERecommendationStatus.Dismissed });
      await recommendation.patchDirty();

      toast(
        t('Dismissed recommendation: {{title}}', {
          title: recommendation.get('title'),
        }),
        { type: 'info' },
      );

      // Optimistically remove the recommendation from the local array immediately
      const index = recommendations.indexOf(recommendation);
      if (index > -1) {
        runInAction(() => {
          recommendations.splice(index, 1);
        });
      }

      // Refresh recommendations in the background to ensure data consistency
      await onRefreshCollection();
      debouncedNotificationRefresh();
    } catch (error) {
      toast(
        t('An error occurred while dismissing the recommendation: {{title}}', {
          title: recommendation.get('title'),
        }),
        { type: 'error' },
      );
    }
  }

  return {
    onDismissRecommendation: handleDismissRecommendation,
  };
}

export default useDismissRecommendation;
